import { useEffect, useRef, useState, useCallback } from "react";

export function useActivityStream({ enabled = true }) {
  const [counts, setCounts] = useState({ messages: 0, tasks: 0, review: 0 });
  const [phase, setPhase] = useState("idle"); // idle | syncing | stable | dismissed
  const readerRef = useRef(null);
  const stableTimerRef = useRef(null);
  const dismissTimerRef = useRef(null);

  const scheduleStable = useCallback(() => {
    clearTimeout(stableTimerRef.current);
    stableTimerRef.current = setTimeout(() => {
      setPhase("stable");
      dismissTimerRef.current = setTimeout(() => setPhase("dismissed"), 5000);
    }, 10000);
  }, []);

  const dismiss = useCallback(() => {
    clearTimeout(stableTimerRef.current);
    clearTimeout(dismissTimerRef.current);
    setPhase("dismissed");
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const token = localStorage.getItem("sw_token");
    if (!token) return;

    const base = process.env.REACT_APP_BACKEND_URL || "";
    let cancelled = false;

    const connect = async () => {
      try {
        const res = await fetch(`${base}/api/activity/stream`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok || !res.body) return;

        const reader = res.body.getReader();
        readerRef.current = reader;
        const decoder = new TextDecoder();
        let buffer = "";

        // Auto-hide if nothing arrives within 20s of connecting
        const idleTimer = setTimeout(() => {
          if (!cancelled) setPhase("dismissed");
        }, 20000);

        while (!cancelled) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";

          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            try {
              const event = JSON.parse(line.slice(6));
              if (event.type === "message") {
                clearTimeout(idleTimer);
                setPhase("syncing");
                setCounts((c) => ({ ...c, messages: c.messages + 1 }));
                scheduleStable();
              } else if (event.type === "task") {
                clearTimeout(idleTimer);
                setPhase("syncing");
                setCounts((c) => ({
                  ...c,
                  tasks: c.tasks + 1,
                  review: c.review + (event.in_review ? 1 : 0),
                }));
                scheduleStable();
              }
            } catch {}
          }
        }
      } catch {}
    };

    connect();

    return () => {
      cancelled = true;
      clearTimeout(stableTimerRef.current);
      clearTimeout(dismissTimerRef.current);
      readerRef.current?.cancel().catch(() => {});
    };
  }, [enabled, scheduleStable]);

  return { counts, phase, dismiss };
}
